import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { LuMoveRight } from "react-icons/lu";
import LazyImage from "../../common/LazyImage";

import { getCategories } from "../../api/api";
import logo from "../../assets/logo.png";


export default function Products() {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const location = useLocation();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCats = async () => {
      try {
        const data = await getCategories();
        setCategories(data);
      } catch (err) {
        console.error("Error fetching categories for home:", err);
      }
    };
    fetchCats();
  }, []);

  useEffect(() => {
    if (location.pathname.includes("/products")) {
      const handleScroll = () => {
        sectionRef.current?.scrollIntoView({ behavior: "smooth" });
      };

      const timeout = setTimeout(handleScroll, 800);

      return () => clearTimeout(timeout);
    }
  }, [location.pathname]);

  const handleCardClick = (catId) => {
    navigate(`/products?cat=${btoa(catId.toString())}`);
  };

  return (
    <section
      id="products"
      ref={sectionRef}
      className="container bg-transparent relative scroll-mt-24 py-10 overflow-visible"
    >
      <div className="w-full">
        <p className="text-[11px] md:text-xs tracking-[0.28em] uppercase text-white/70">
          Products
        </p>
        <h2 className="mt-3 text-3xl md:text-5xl font-extrabold text-white leading-tight">
          Explore Our <span className="text-secondary italic">Product Categories.</span>
        </h2>
        <p className="mt-4 text-white/80 md:text-lg leading-relaxed max-w-3xl">
          Explore our core categories. Click a tile to view subcategories and
          product details on the Products page.
        </p>
        <div className="mt-8 flex gap-3">
          <button
            onClick={() => navigate("/products")}
            className="inline-flex items-center gap-3 justify-center px-5 py-3 rounded-md border border-white/20 text-white hover:bg-white/10"
          >
            View All Products <LuMoveRight className="w-5 h-5" />
          </button>
        </div>
        {categories.length > 0 ? (
          <div className="mt-10 grid gap-2 grid-cols-2 sm:grid-cols-2 lg:grid-cols-3">
            {categories.map((c) => (
              <motion.div
                key={c.categoryId}
                onClick={() => handleCardClick(c.categoryId)}
                className="group relative h-[300px] sm:h-[400px] lg:h-[500px] w-full overflow-hidden border border-white/10 bg-white/[0.04] cursor-pointer"
                whileHover={{ scale: 1.01 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
              >
                <div className="absolute inset-0 h-full w-full bg-neutral-900">
                  {c.image ? (
                    <LazyImage
                      src={c.image}
                      alt={c.categoryName}
                      className="absolute inset-0 h-full w-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-[0.8]"
                    />
                  ) : (
                    <div className="flex h-full w-full flex-col items-center justify-center gap-4">
                        <img src={logo} alt="" className="h-16 opacity-20" />
                        <span className="text-[10px] tracking-widest text-white/10 font-black uppercase">No Preview</span>
                    </div>
                  )}
                </div>

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-70 group-hover:opacity-90 transition-all duration-700" />

                <div className="absolute inset-0 flex items-end p-8">
                  <div className="transition-all duration-700 opacity-0 translate-y-6 group-hover:opacity-100 group-hover:translate-y-0">
                    <h3 className="text-2xl font-bold text-white drop-shadow-md mb-3">
                      {c.categoryName}
                    </h3>
                    <p className="text-white/85 text-xs leading-relaxed max-w-md uppercase tracking-[0.1em] font-medium">
                      Explore our {c.categoryName} Systems
                    </p>
                  </div>
                </div>

                <div className="absolute inset-0 border border-white/10 group-hover:border-white/30 transition-all duration-700"></div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="mt-10 py-32 text-center border border-dashed border-white/10 bg-white/[0.02] rounded-[40px] backdrop-blur-sm">
            <div className="w-20 h-20 bg-white/[0.03] rounded-full flex items-center justify-center mx-auto mb-6 border border-white/5 animate-pulse">
              <i className="ri-folder-info-line text-4xl text-white/20"></i>
            </div>
            <p className="text-white/40 font-black uppercase tracking-[0.4em] text-[11px]">System definitions coming soon</p>
            <div className="w-12 h-1 bg-white/5 mx-auto mt-6" />
          </div>
        )}
      </div>
    </section>
  );
}
